import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { anyService } from "@/services/breeze/AnyService";
import { entityService } from "@/services/breeze/EntityService";
import CalendarView, {
  CalendarEvent,
} from "@/components/calendar/CalendarView";
import { Field } from "@/types/breeze/Field";
import { useAuth } from "@/providers/AuthProvider";
import { Card } from "@radix-ui/themes";

interface EntityCalendarViewProps {
  entityName: string;
  className?: string;
}

export default function EntityCalendarView({
  entityName,
  className = "",
}: EntityCalendarViewProps) {
  const { currentTenant, safeNavigate } = useAuth();
  const [dateFields, setDateFields] = useState<Field[]>([]);

  // Fetch entity metadata to identify date fields
  const { data: entity } = useQuery({
    queryKey: ["entity", entityName],
    queryFn: () => entityService.getByName(entityName),
  });

  // Fetch entity data
  const { data, isLoading } = useQuery({
    queryKey: ["entity-list", entityName],
    queryFn: () => anyService.getAll(entityName, ["*"], undefined, 1, 1000),
  });

  // Find date fields in the entity
  useEffect(() => {
    if (entity) {
      // Identify date fields that could be used for calendar events
      const dateCandidates = entity.fields.filter(
        (field) =>
          field.database_type === "date" ||
          field.database_type === "timestamp" ||
          field.name.toLowerCase().includes("date") ||
          field.name.toLowerCase().includes("time") ||
          field.name.toLowerCase().includes("start") ||
          field.name.toLowerCase().includes("end"),
      );

      setDateFields(dateCandidates);
    }
  }, [entity]);

  // Handle event click
  const handleEventClick = (event: CalendarEvent) => {
    if (event.resource?.id) {
      safeNavigate(
        `/org/${currentTenant?.id}/entities/${entityName}/${event.resource.id}`,
      );
    }
  };

  // Don't render until we have entity metadata and date fields
  if (!entity || !dateFields.length) {
    return (
      <Card>
        {isLoading
          ? "Loading data..."
          : "This entity doesn't have date fields suitable for calendar view."}
      </Card>
    );
  }

  // Find the most suitable fields for the calendar
  const startDateField =
    dateFields.find(
      (f) =>
        f.name.toLowerCase().includes("start") ||
        f.name.toLowerCase() === "date",
    )?.name || dateFields[0]?.name;

  const endDateField =
    dateFields.find(
      (f) =>
        f.name.toLowerCase().includes("end") ||
        f.name.toLowerCase() === "due_date",
    )?.name || startDateField;

  const titleField =
    entity.fields.find(
      (f) =>
        f.name.toLowerCase() === "title" || f.name.toLowerCase() === "name",
    )?.name || "id";

  const descriptionField = entity.fields.find(
    (f) =>
      f.name.toLowerCase() === "description" ||
      f.name.toLowerCase() === "notes",
  )?.name;

  const geoField = entity.fields.find(
    (f) =>
      f.name.toLowerCase() === "geo" || f.name.toLowerCase() === "coordinates",
  )?.name;

  // Extract data from paginated result
  const items = data?.items || [];

  return (
    <CalendarView
      data={items}
      isLoading={isLoading}
      startDateField={startDateField}
      endDateField={endDateField}
      titleField={titleField}
      descriptionField={descriptionField}
      geoField={geoField}
      onEventClick={handleEventClick}
      className={className}
      entityName={entityName}
    />
  );
}
